import React, {useCallback, useEffect, useRef, useState} from 'react';
import {StyleSheet, Text, View} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';
import {useAuth} from '../../context/AuthContext';
import globalStyle from '../../theme/globalStyle';
import {useTheme} from '../../theme/ThemeContext';
import {
  getAuthPromptDismissed,
  setAuthPromptDismissed,
} from '../../utils/storage';
import UIButton from '../ui-components/UIButton';
import UIModal from '../ui-components/UIModal';
import {FORCE_AUTH_FIRST_LAUNCH_MODAL} from '../../config/authDebug';

/**
 * Shown once per install (until dismissed) when Supabase is configured and the user is signed out.
 */
const AuthFirstLaunchModal = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {theme} = useTheme();
  const global = globalStyle(theme);
  const {user, loading, isConfigured, suppressAuthPromptAfterSignOut} =
    useAuth();
  const [storageLoaded, setStorageLoaded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);
  /** Hide until next app launch after user opens Auth from this modal (no AsyncStorage). */
  const suppressedForSessionRef = useRef(false);
  /** While FORCE_AUTH_FIRST_LAUNCH_MODAL: hide after dismiss until next reload. */
  const forceDismissedThisRunRef = useRef(false);

  useEffect(() => {
    if (FORCE_AUTH_FIRST_LAUNCH_MODAL) {
      setStorageLoaded(true);
      return undefined;
    }
    let cancelled = false;
    getAuthPromptDismissed().then(v => {
      if (!cancelled) {
        setDismissed(v);
        setStorageLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const ready = FORCE_AUTH_FIRST_LAUNCH_MODAL || storageLoaded;
    if (!ready) {
      setVisible(false);
      return;
    }
    if (FORCE_AUTH_FIRST_LAUNCH_MODAL) {
      if (forceDismissedThisRunRef.current) {
        setVisible(false);
        return;
      }
    } else {
      if (
        dismissed ||
        suppressedForSessionRef.current ||
        suppressAuthPromptAfterSignOut
      ) {
        setVisible(false);
        return;
      }
    }
    if (!isConfigured || loading || user) {
      setVisible(false);
      return;
    }
    setVisible(true);
  }, [
    storageLoaded,
    dismissed,
    isConfigured,
    loading,
    user,
    suppressAuthPromptAfterSignOut,
  ]);

  const handleDismiss = useCallback(async () => {
    if (FORCE_AUTH_FIRST_LAUNCH_MODAL) {
      forceDismissedThisRunRef.current = true;
      setVisible(false);
      return;
    }
    await setAuthPromptDismissed();
    setDismissed(true);
    setVisible(false);
  }, []);

  const openAuth = useCallback(() => {
    if (!FORCE_AUTH_FIRST_LAUNCH_MODAL) {
      suppressedForSessionRef.current = true;
    }
    setVisible(false);
    navigation.navigate('Auth');
  }, [navigation]);

  const styles = getStyles(theme);

  return (
    <UIModal
      visible={visible}
      onClose={handleDismiss}
      modalTitle={t('AUTH_PROMPT_TITLE')}
      titleStyle={styles.titleStyle}>
      <Text style={[global.textMain, styles.body]}>{t('AUTH_PROMPT_BODY')}</Text>
      <View style={styles.actionsRow}>
        <UIButton
          title={t('AUTH_SIGN_IN')}
          type="outline"
          style={styles.btn}
          onPress={openAuth}
        />
        <UIButton title={t('AUTH_SIGN_UP')} style={styles.btn} onPress={openAuth} />
      </View>
      <View style={global.modalFooter}>
        <UIButton
          title={t('AUTH_PROMPT_NOT_NOW')}
          type="outline"
          style={styles.btnWide}
          onPress={handleDismiss}
        />
      </View>
    </UIModal>
  );
};

const getStyles = theme =>
  StyleSheet.create({
    titleStyle: {
      color: theme.colors.primary,
    },
    body: {
      fontSize: 16,
      lineHeight: 22,
      marginBottom: 8,
    },
    actionsRow: {
      flexDirection: 'row',
      justifyContent: 'center',
      flexWrap: 'wrap',
      marginTop: 8,
    },
    btn: {
      marginHorizontal: 5,
      marginTop: 8,
      minWidth: 110,
    },
    btnWide: {
      minWidth: 160,
    },
  });

export default AuthFirstLaunchModal;
